import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  MenuItem,
  Select,
  Snackbar,
  Alert,
  Box
} from '@mui/material';
import Cookies from 'js-cookie';

// Rank típus definíció
interface Rank {
  name: string;
  price: number;
  description: string;
  rankDescription: string;
}

interface AdminRankProps {
  showNotification: (severity: 'success' | 'error', message: string) => void;
  fetchRanks?: () => void;
}

const AdminRank: React.FC<AdminRankProps> = ({ showNotification, fetchRanks }) => {
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [ranks, setRanks] = useState<Rank[]>([]);
  const [selectedRank, setSelectedRank] = useState<string>('');
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    rankDescription: ''
  });

  // Rankok lekérése
  useEffect(() => {
    const loadRanks = async () => {
      try {
        const token = Cookies.get('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/user/getallranks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        setRanks(data);
      } catch (error) {
        showNotification('error', 'Error fetching ranks');
      }
    };
    loadRanks();
  }, []);

  const handleCreateRank = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/createrank`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price)
        })
      });

      if (response.ok) {
        showNotification('success', 'Rank created successfully!');
        setOpenCreate(false);
        setFormData({ name: '', price: '', description: '', rankDescription: '' });
        // Frissítés ha szükséges
        if (fetchRanks) fetchRanks();
      } else {
        showNotification('error', 'Rank creation failed');
      }
    } catch (error) {
      showNotification('error', 'Error creating rank');
    }
  };

  const handleEditRank = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/admin/editranks`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          price: parseFloat(formData.price)
        })
      });

      if (response.ok) {
        showNotification('success', 'Rank updated successfully!');
        setOpenEdit(false);
        setSelectedRank('');
        setFormData({ name: '', price: '', description: '', rankDescription: '' });
        // Frissítés ha szükséges
        if (fetchRanks) fetchRanks();
      } else {
        showNotification('error', 'Rank update failed');
      }
    } catch (error) {
      showNotification('error', 'Error updating rank');
    }
  };

  return (
    <>
      {/* Create Rank Dialog */}
      <Dialog open={openCreate} onClose={() => setOpenCreate(false)}>
        <DialogTitle>Create New Rank</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Price"
            fullWidth
            required
            type="number"
            value={formData.price}
            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            required
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          />
          <TextField
                margin="dense"
                label="Rank Description"
                fullWidth
                multiline
                minRows={4}
                maxRows={8}
                value={formData.rankDescription}
                onChange={(e) => setFormData(prev => ({ ...prev, rankDescription: e.target.value }))}
                sx={{
                    '& .MuiOutlinedInput-root': {
                    padding: '10px',
                    fontSize: '0.9rem',
                    fontFamily: 'monospace',
                    '& textarea': {
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.5'
                    }
                    },
                    mt: 2,
                    mb: 1
                }}
                helperText="Use emojis (🎮✨) and formatting (** bold**) for text"
                />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={handleCreateRank} color="primary">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Rank Dialog */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>Edit Rank</DialogTitle>
        <DialogContent>
        <Select
            fullWidth
            value={selectedRank}
            onChange={(e) => {
                const rankName = e.target.value as string;
                const rank = ranks.find(r => r.name === rankName);
                if (rank) {
                setSelectedRank(rankName);
                setFormData({
                    name: rank.name,
                    price: rank.price.toString(),
                    description: rank.description || '', 
                    rankDescription: rank.rankDescription || '' 
                });
                }
            }}
            displayEmpty
            sx={{ mb: 2 }}
            >
            <MenuItem value="" disabled>Select Rank</MenuItem>
            {ranks.map((rank) => (
                <MenuItem key={rank.name} value={rank.name}>{rank.name}</MenuItem>
            ))}
            </Select>
          
          <TextField
            margin="dense"
            label="Price"
            fullWidth
            type="number"
            value={formData.price}
            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Rank Description"
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            value={formData.rankDescription}
            onChange={(e) => setFormData(prev => ({ ...prev, rankDescription: e.target.value }))}
            sx={{
                '& .MuiOutlinedInput-root': {
                padding: '10px',
                fontSize: '0.9rem',
                fontFamily: 'monospace',
                '& textarea': {
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.5'
                }
                },
                mt: 2,
                mb: 1
            }}
            helperText="Use emojis (🎮✨) and formatting (** bold**) for text"
            />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenEdit(false);
            setSelectedRank('');
            setFormData({ name: '', price: '', description: '', rankDescription: '' });
          }}>Cancel</Button>
          <Button onClick={handleEditRank} color="primary">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Gombok */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button variant="contained" color="primary" onClick={() => setOpenCreate(true)}>
          Create Rank
        </Button>
        <Button variant="contained" color="primary" onClick={() => setOpenEdit(true)}>
          Edit Rank
        </Button>
      </Box>
    </>
  );
};

export default AdminRank;